import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EntryFeedback } from '../conversationSlice';
import { isSameLandmine, withoutDuplicates } from '../../utils/sessionHistory';
import { relabelMovedAlerts } from '../../utils/participantLinesMoved';
import { relabelAlertsOf } from '../../utils/participantRoleChange';
import { participantLinesMoved, participantReclassified } from '../participantActions';

interface LandmineAlert {
  trigger: string;
  matched_text: string;
  matched_phrase: string;  // The phrase that semantically matched
  similarity_score: number;  // Confidence 0.0-1.0 (e.g., 0.85 = 85% match)
  semantic_match: boolean;  // True for vector match, false for exact
  landmine_type: 'legal_risk' | 'detractor';  // Type of landmine
  category: string;
  priority: 'high' | 'medium' | 'low';
  can_say: Array<{
    point: string;
    context?: string;
  }>;
  cannot_say: Array<{
    point: string;
    reason?: string;
    severity?: 'legal_risk' | 'compliance_risk' | 'brand_risk' | 'general';
  }>;
  quick_tips?: string;
  detected_at: string;
  timestamp?: string;
  speaker: string;
  /** Who said the line that raised it (standalone calls send it). */
  participantId?: string;
  speakerName?: string;
  feedback?: EntryFeedback;
  /** Set only by the history restore (watch-join, reconnect, owner resume).
   *  LandmineBar adds these silently: no red header, strip or badge pulse. */
  isRestored?: boolean;
}

/** A landmine as the store holds it (live or restored). */
export type LandmineAlertRecord = LandmineAlert;

interface LandmineState {
  alerts: LandmineAlert[]; // Landmines for LandmineBar, in arrival order (oldest first)
  allAlerts: LandmineAlert[]; // All accumulated alerts for session saving
  isConnected: boolean;
  connectionError: string | null;
}

const initialState: LandmineState = {
  alerts: [],
  allAlerts: [], // Store all alerts for session saving
  isConnected: false,
  connectionError: null
};

const landmineSlice = createSlice({
  name: 'landmine',
  initialState,
  reducers: {
    addLandmineAlert: {
      reducer: (state, action: PayloadAction<LandmineAlert>) => {
        // Already shown as history (the saved session got here first): keep the
        // silent restored row, do not add a second, alerting one.
        if (state.alerts.some((existing) => existing.isRestored && isSameLandmine(existing, action.payload))) return;
        // Always recorded for the session: the words were said.
        state.allAlerts.push(action.payload);
        // Repeats are gated server-side (ALERT_REPEAT_COOLDOWN_SECONDS in the
        // landmine and competitor services), so every alert that arrives is shown.
        // APPENDED: LandmineBar renders this array as-is, oldest first. It does
        // not sort by detected_at: an earlier version did and still showed new
        // landmines at the top in testing, and arrival order needs no timestamp
        // at all — it is the order the rep experienced. A restored
        // session dispatches its rows in server order, which this keeps.
        state.alerts.push(action.payload);
      },
      // An alert that arrived without a clock is stamped here (feedback and
      // the summary key on detected_at) — the one impure step, kept out of the
      // reducer.
      prepare: (alert: LandmineAlert) => ({
        payload: alert.detected_at ? alert : { ...alert, detected_at: new Date().toISOString() },
      }),
    },
    /**
     * History restore: the saved session's landmines, minus any already here,
     * APPENDED in server order and marked `isRestored`, so LandmineBar lists
     * them without the arrival cue. Appending keeps the bar's rule that rows
     * are only ever appended (their index is their identity).
     */
    restoreLandmineHistory: (state, action: PayloadAction<LandmineAlert[]>) => {
      const missing = withoutDuplicates(action.payload || [], state.alerts, isSameLandmine);
      missing.forEach((alert) => {
        const restored = { ...alert, isRestored: true };
        state.allAlerts.push(restored);
        state.alerts.push(restored);
      });
    },
    clearLandmineAlerts: (state) => {
      state.alerts = [];
      state.allAlerts = [];
    },
    setLandmineConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
      if (action.payload) {
        state.connectionError = null;
      }
    },
    setLandmineConnectionError: (state, action: PayloadAction<string | null>) => {
      state.connectionError = action.payload;
    },
    updateLandmineFeedback: (state, action: PayloadAction<{ detected_at: string; feedback: EntryFeedback }>) => {
      const updateIn = (arr: LandmineAlert[]) => {
        const alert = arr.find(a => a.detected_at === action.payload.detected_at);
        if (alert) alert.feedback = action.payload.feedback;
      };
      updateIn(state.alerts);
      updateIn(state.allAlerts);
    }
  },
  extraReducers: (builder) => {
    // Raised by a line that moved to a newcomer: relabelled, never removed.
    builder.addCase(participantLinesMoved, (state, action) => {
      state.alerts = relabelMovedAlerts(state.alerts, action.payload);
      state.allAlerts = relabelMovedAlerts(state.allAlerts, action.payload);
    });
    // A human decided who the raiser is: their alerts take the role and name, never removed.
    builder.addCase(participantReclassified, (state, action) => {
      state.alerts = relabelAlertsOf(state.alerts, action.payload);
      state.allAlerts = relabelAlertsOf(state.allAlerts, action.payload);
    });
  }
});

export const {
  addLandmineAlert,
  restoreLandmineHistory,
  clearLandmineAlerts,
  setLandmineConnectionStatus,
  setLandmineConnectionError,
  updateLandmineFeedback
} = landmineSlice.actions;

export default landmineSlice.reducer;